import {
  SchedulerFormSubmitPayload,
  SchedulerNeedHelpPayload,
  SchedulerStructuredSubmitPayload,
} from '@src/types/scheduler-message';
import { TaskTriggerType } from '@src/types';

const SCHEDULER_FORM_PREFIX = '__SCHEDULER_FORM__';

export const encodeSchedulerPayload = (payload: object) =>
  encodeURIComponent(JSON.stringify(payload));

export const decodeSchedulerPayload = <T>(payload?: string): T | null => {
  if (!payload) {
    return null;
  }
  try {
    return JSON.parse(decodeURIComponent(payload));
  } catch {
    return null;
  }
};

export const toSchedulerFormPrompt = (
  payload: SchedulerFormSubmitPayload,
): string => {
  const structuredPayload = toSchedulerStructuredPayload(payload);
  return [SCHEDULER_FORM_PREFIX, JSON.stringify(structuredPayload)].join('\n');
};

const formatWeekday = (day: number) => {
  const weekdayMap: Record<number, string> = {
    0: '周日',
    1: '周一',
    2: '周二',
    3: '周三',
    4: '周四',
    5: '周五',
    6: '周六',
    7: '周日',
  };
  return weekdayMap[day] || `周${day}`;
};

const normalizeDateTime = (value?: string) => {
  return value?.trim() || '';
};

const getSchedulerTriggerDescription = (
  payload: SchedulerStructuredSubmitPayload,
) => {
  const triggerType = payload.trigger_type;
  const triggerConfig = payload.trigger_config || {};
  if (triggerType === TaskTriggerType.ONCE) {
    const datetime = normalizeDateTime(triggerConfig.once_config?.datetime);
    return datetime ? `在 ${datetime} 执行` : '执行一次';
  }
  if (triggerType === TaskTriggerType.EVERY_DAY) {
    const datetime = normalizeDateTime(triggerConfig.every_day_config?.datetime);
    return datetime ? `每天 ${datetime}` : '每天执行';
  }
  if (triggerType === TaskTriggerType.EVERY_WEEK) {
    const datetime = normalizeDateTime(triggerConfig.every_week_config?.datetime);
    const dayText = (triggerConfig.every_week_config?.days || [])
      .map((day) => formatWeekday(day))
      .join('、');
    if (dayText && datetime) {
      return `${dayText} ${datetime}`;
    }
    if (dayText) {
      return dayText;
    }
    return datetime ? `每周 ${datetime}` : '每周执行';
  }
  if (triggerType === TaskTriggerType.EVERY_MONTH) {
    const datetime = normalizeDateTime(triggerConfig.every_month_config?.datetime);
    const dayText = (triggerConfig.every_month_config?.days || []).join('、');
    if (dayText && datetime) {
      return `每月 ${dayText} 号 ${datetime}`;
    }
    if (dayText) {
      return `每月 ${dayText} 号`;
    }
    return datetime ? `每月 ${datetime}` : '每月执行';
  }
  return '';
};

export const toSchedulerDisplayText = (
  payload: SchedulerStructuredSubmitPayload,
) => {
  const taskText = (payload.task || payload.title || '定时任务').trim();
  const triggerText = getSchedulerTriggerDescription(payload);
  if (!triggerText) {
    return `帮我创建定时任务：${taskText}`;
  }
  return `帮我创建定时任务：${taskText}，${triggerText}`;
};

export const toSchedulerFormDisplayText = (
  payload: SchedulerFormSubmitPayload,
) => {
  return toSchedulerDisplayText(toSchedulerStructuredPayload(payload));
};

export const parseSchedulerFormPrompt = (
  message?: string,
): SchedulerStructuredSubmitPayload | null => {
  const content = message?.trim();
  if (!content || !content.startsWith(SCHEDULER_FORM_PREFIX)) {
    return null;
  }
  const payloadText = content.slice(SCHEDULER_FORM_PREFIX.length).trim();
  if (!payloadText) {
    return null;
  }
  try {
    return JSON.parse(payloadText);
  } catch {
    return null;
  }
};

export const toSchedulerDisplayTextFromPrompt = (message?: string) => {
  const structuredPayload = parseSchedulerFormPrompt(message);
  if (!structuredPayload) {
    return message || '';
  }
  return toSchedulerDisplayText(structuredPayload);
};

export const toSchedulerStructuredPayload = (
  payload: SchedulerFormSubmitPayload,
): SchedulerStructuredSubmitPayload => {
  const title = (payload.title || payload.task || '定时任务').trim();
  const triggerType = payload.triggerType;
  const triggerConfig: SchedulerStructuredSubmitPayload['trigger_config'] = {};

  if (triggerType === TaskTriggerType.ONCE) {
    triggerConfig.once_config = {
      datetime: payload.datetime,
    };
  }
  if (triggerType === TaskTriggerType.EVERY_DAY) {
    triggerConfig.every_day_config = {
      datetime: payload.datetime,
    };
  }
  if (triggerType === TaskTriggerType.EVERY_WEEK) {
    triggerConfig.every_week_config = {
      datetime: payload.datetime,
      days: payload.weekDays || [],
    };
  }
  if (triggerType === TaskTriggerType.EVERY_MONTH) {
    triggerConfig.every_month_config = {
      datetime: payload.datetime,
      days: payload.monthDays || [],
    };
  }
  const structuredPayload = {
    help_id: payload.helpId,
    title: title || '定时任务',
    task: payload.task,
    trigger_type: payload.triggerType,
    trigger_config: triggerConfig,
    enabled: true,
  };
  return structuredPayload;
};

export const toSchedulerFormInitialValues = (
  payload: SchedulerNeedHelpPayload,
) => {
  const triggerConfig = payload.intent_clarification?.trigger_config;
  const triggerType = triggerConfig?.trigger_type || 'once';

  let datetime = '';
  if (triggerType === 'once') {
    if (triggerConfig?.trigger_date && triggerConfig?.trigger_time) {
      datetime = `${triggerConfig.trigger_date} ${triggerConfig.trigger_time}`;
    } else if (triggerConfig?.trigger_date) {
      datetime = triggerConfig.trigger_date;
    } else if (triggerConfig?.trigger_time) {
      datetime = triggerConfig.trigger_time;
    }
  } else if (triggerConfig?.trigger_time) {
    datetime = triggerConfig.trigger_time;
  }

  return {
    task: payload.intent_clarification?.task || '',
    title: payload.intent_clarification?.title || '',
    triggerType: triggerType,
    datetime: datetime,
    weekDays: triggerConfig?.weekdays || [],
    monthDays: triggerConfig?.days_of_month || [],
  };
};
